import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Option = {
  label: string;
  value: string;
};

type Props = {
  name: string;
  label: string;
  placeholder?: string;
  options: Option[];
};

const RHFAutocomplete = ({ name, label, placeholder, options }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <FormControl fullWidth error={!!errors[name]}>
      {label && (
        <FormLabel
          sx={(theme) => ({
            mb: 0.75,
            color: theme.palette.primary.main,
            fontSize: "12px",
            "&.Mui-error": {
              color: theme.palette.primary.main,
            },
          })}
        >
          {t(label)}
        </FormLabel>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Autocomplete
            options={options}
            value={
              options.find((option) => option.value === field.value) ?? null
            }
            onChange={(_, value) => field.onChange(value?.value ?? "")}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            getOptionLabel={(option) => option.label}
            slotProps={{
              paper: {
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "14px",
                  },
                },
              },
            }}
            renderInput={(params) => {
              const { InputLabelProps, ...rest } = params;

              return (
                <TextField
                  {...rest}
                  size="small"
                  placeholder={t(placeholder || "select")}
                  error={!!errors[name]}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  sx={(theme) => ({
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#3A3A3A",
                      borderRadius: 1,
                      color: "#fff",

                      "& fieldset": {
                        borderColor: "#555",
                      },

                      "&:hover fieldset": {
                        borderColor: "#777",
                      },

                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },

                    "& .MuiAutocomplete-input": {
                      fontSize: "14px",
                    },
                  })}
                />
              );
            }}
          />
        )}
      />

      <FormHelperText>
        {t((errors[name]?.message as string) || "")}
      </FormHelperText>
    </FormControl>
  );
};

export default RHFAutocomplete;
